import React from "react";
import moment from "moment";
import "./calendar.css";
import { Calendar } from "@/global/base/calendar.tsx";

/** @param {unknown} value */
const parsePropDate = (value) => {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }
  const d =
    value instanceof Date ? new Date(value.getTime()) : new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d;
};

/**
 * Single-date picker using react-day-picker (see `global/base/calendar.tsx`).
 * Does not import RSuite CSS, so global button styles are unaffected.
 *
 * `selectRange` is kept for API compatibility with older call sites; range mode
 * is not used in this app (all callers pass false).
 */
const CalendarComponent = ({
  close,
  date,
  selectRange: _selectRange,
  setDateRaw,
  maxDate,
  onResetCalendar = () => {},
  onClose = () => {},
}) => {
  const dismiss = React.useCallback(() => {
    if (typeof close === "function") {
      close(false);
    }
  }, [close]);

  const onCloseRef = React.useRef(onClose);
  React.useEffect(() => {
    onCloseRef.current = onClose;
  });
  React.useEffect(() => {
    return () => {
      const fn = onCloseRef.current;
      if (typeof fn === "function") {
        fn();
      }
    };
  }, []);

  const selected = parsePropDate(date);
  const [month, setMonth] = React.useState(() => selected || new Date());

  React.useEffect(() => {
    const p = parsePropDate(date);
    if (p) {
      setMonth(p);
    }
  }, [date]);

  const disabledDays = maxDate
    ? (day) => moment(day).isAfter(maxDate, "day")
    : undefined;

  const handleSelect = (nextDate) => {
    if (!nextDate) {
      return;
    }
    setDateRaw(nextDate);
    dismiss();
  };

  const selectToday = () => {
    let t = new Date();
    if (maxDate && moment(t).isAfter(maxDate, "day")) {
      t = maxDate;
    }
    handleSelect(t);
  };

  const handleClear = () => {
    onResetCalendar();
    dismiss();
  };

  return (
    <div
      className="react-calendar-div flex flex-col overflow-hidden rounded-lg border border-[#e5e5e5] bg-white shadow-lg"
      data-testid="calendar"
    >
      <Calendar
        mode="single"
        month={month}
        onMonthChange={setMonth}
        selected={selected}
        onSelect={handleSelect}
        disabled={disabledDays}
        initialFocus
        className="rounded-t-lg"
      />
      <div
        className="flex items-center justify-between gap-2 border-t border-[#f0f0f0] px-3 py-2"
        data-testid="calendar-actions"
      >
        <button
          type="button"
          className="text-sm font-medium text-[#00567a] hover:underline"
          onClick={handleClear}
          data-testid="calendar-clear"
        >
          Clear
        </button>
        <button
          type="button"
          className="text-sm font-medium text-[#00567a] hover:underline"
          onClick={selectToday}
          data-testid="calendar-today"
        >
          Today
        </button>
      </div>
    </div>
  );
};

export default CalendarComponent;
